import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import {
  ShoppingCart,
  DollarSign,
  Users,
  Package,
  TrendingUp,
  ArrowRight,
  ShieldCheck,
  Clock,
  CheckCircle,
  Truck,
  Building2
} from "lucide-react";
import { api } from "../../services/api";
import { formatPrice } from "../../utils/formatPrice";
import { StatusBadge, ORDER_STATUS } from "../../utils/orderStatus";
import { Skeleton } from "../../components/ui/skeleton";
import { useLanguage } from "../../context/LanguageContext";

const PIE_COLORS = ["#D97706", "#2563EB", "#7C3AED", "#059669", "#16A34A", "#DC2626"];

function KpiCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#EAE8E4] p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div
          className="grid place-items-center rounded-xl"
          style={{ width: 44, height: 44, background: accent + "18", color: accent }}
        >
          <Icon size={20} />
        </div>
        {sub && <span className="text-[11px] font-mono text-[#888888] bg-[#F8F8F6] px-2 py-0.5 rounded-full">{sub}</span>}
      </div>
      <p className="font-display text-[#111111] mt-4 text-3xl font-semibold leading-tight">{value}</p>
      <p className="text-xs text-[#666666] font-body mt-1 uppercase tracking-wider">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch((err) => console.error("Error loading admin stats:", err));
  }, []);

  if (!stats) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64 rounded-xl" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const pieData = Object.entries(stats.ordersByStatus || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({
      name: ORDER_STATUS[k]?.label || k,
      value: v,
      key: k,
    }));

  const avgOrderValue = stats.totalOrders > 0
    ? Math.round(stats.totalRevenue / stats.totalOrders)
    : 0;

  return (
    <div className="space-y-8">
      
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-[#EAE8E4]">
        <div>
          <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#D97706] font-semibold flex items-center gap-1.5">
            <ShieldCheck size={14} />
            Maison The Queen — Haute Dermatologie
          </span>
          <h1 className="font-display text-[#111111] text-3xl sm:text-4xl mt-1">
            Tableau de Bord CRM &amp; Statistiques
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/orders")}
            className="btn-queen-primary text-xs h-10 px-5 shadow-sm flex items-center gap-2"
          >
            <ShoppingCart size={14} />
            <span>Gérer les Commandes ({stats.totalOrders})</span>
          </button>
        </div>
      </div>

      {/* 4 Master KPIs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard
          icon={DollarSign}
          label="Chiffre d'Affaires Total"
          value={formatPrice(stats.totalRevenue)}
          sub="Algérie 58 Wilayas"
          accent="#16A34A"
        />
        <KpiCard
          icon={ShoppingCart}
          label="Commandes Reçues"
          value={stats.totalOrders}
          sub={`${stats.ordersByStatus?.pending || 0} en attente`}
          accent="#D97706"
        />
        <KpiCard
          icon={TrendingUp}
          label="Panier Moyen"
          value={formatPrice(avgOrderValue)}
          sub="Par commande"
          accent="#2563EB"
        />
        <KpiCard
          icon={Users}
          label="Clients Enregistrés"
          value={stats.totalUsers}
          sub="Actifs"
          accent="#7C3AED"
        />
      </div>

      {/* Middle Row: Revenue Chart & Status Breakdown */}
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
        
        {/* Revenue 14 Days Area Chart */}
        <div className="bg-white rounded-2xl border border-[#EAE8E4] p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} className="text-[#D97706]" />
              <h2 className="font-display text-xl text-[#111111]">
                Évolution des Revenus (14 derniers jours)
              </h2>
            </div>
            <span className="text-[11px] font-mono text-[#888888] bg-[#F8F8F6] px-2.5 py-1 rounded-full">
              DZD
            </span>
          </div>

          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats.revenueSeries || []} margin={{ left: -10, right: 10, top: 10 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D97706" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#D97706" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#888888" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#888888" }} axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(v) => [formatPrice(v), "Revenus"]}
                contentStyle={{
                  borderRadius: 12,
                  border: "1px solid #EAE8E4",
                  fontFamily: "DM Sans",
                  fontSize: "12px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                }}
              />
              <Area type="monotone" dataKey="revenue" stroke="#D97706" strokeWidth={2.5} fill="url(#revGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Orders by Status Pie */}
        <div className="bg-white rounded-2xl border border-[#EAE8E4] p-6 shadow-sm flex flex-col justify-between">
          <div>
            <h2 className="font-display text-xl text-[#111111] mb-2">
              Répartition par Statut
            </h2>
            <p className="text-xs text-[#888888] mb-4">Cycle de traitement logistique</p>

            {pieData.length === 0 ? (
              <div className="text-center py-16 text-[#888888] text-sm font-body">
                Aucune commande enregistrée pour le moment.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={4}
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #EAE8E4",
                      fontFamily: "DM Sans",
                      fontSize: "12px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-4 border-t border-[#EAE8E4]">
            {pieData.map((d, i) => (
              <span
                key={d.key}
                className="inline-flex items-center gap-1.5 text-[11px] font-body bg-[#FAF8F5] px-2.5 py-1 rounded-full border border-[#EAE8E4]"
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                />
                <span className="text-[#555555]">{d.name} :</span>
                <strong className="text-[#111111]">{d.value}</strong>
              </span>
            ))}
          </div>
        </div>

      </div>

      {/* Bottom Row: Recent Orders Table */}
      <div className="bg-white rounded-2xl border border-[#EAE8E4] p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-display text-2xl text-[#111111]">
              Commandes Récentes en Direct
            </h2>
            <p className="text-xs text-[#888888] mt-0.5">
              Cliquez sur une commande pour la consulter ou modifier son statut dans le CRM
            </p>
          </div>
          <button
            onClick={() => navigate("/admin/orders")}
            className="text-xs font-semibold text-[#111111] hover:text-[#D97706] flex items-center gap-1 transition-colors"
          >
            <span>Voir toutes ({stats.totalOrders})</span>
            <ArrowRight size={14} />
          </button>
        </div>

        {(!stats.recentOrders || stats.recentOrders.length === 0) ? (
          <p className="text-sm text-[#888888] py-8 text-center font-body">
            Aucune commande reçue pour l'instant.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left font-body">
              <thead>
                <tr className="border-b border-[#EAE8E4] text-[11px] uppercase tracking-wider text-[#888888] font-medium bg-[#FAF8F5]">
                  <th className="py-3 px-4">N° Commande</th>
                  <th className="py-3 px-4">Cliente / Client</th>
                  <th className="py-3 px-4">Wilaya</th>
                  <th className="py-3 px-4">Montant Total</th>
                  <th className="py-3 px-4">Statut</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EAE8E4]/60">
                {stats.recentOrders.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => navigate("/admin/orders")}
                    className="hover:bg-[#FAF8F5] cursor-pointer transition-colors"
                  >
                    <td className="py-3 px-4 font-mono font-medium text-[#111111]">
                      #{o.order_number}
                    </td>
                    <td className="py-3 px-4">
                      <p className="font-medium text-[#111111]">{o.customer_name}</p>
                      <p className="text-xs text-[#777777] font-mono">{o.customer_phone}</p>
                    </td>
                    <td className="py-3 px-4 text-[#555555]">
                      {o.wilaya || "Algérie"}
                    </td>
                    <td className="py-3 px-4 font-medium text-[#111111]">
                      {formatPrice(o.total)}
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={o.status} />
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-xs text-[#D97706] font-medium hover:underline">
                        Détails →
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
